/**
 * View script for interact/media:
 *   - Branded loader animation (.wp-block-interact-media[data-loader="branded"])
 *   - Click-to-play overlay (.wp-block-interact-media[data-player="video"])
 */

// Minimum dwell so the brand animation always reads, even on fast networks.
const MIN_MS = 2600;
// Safety net for stalled loads (slow CDN, autoplay blocked, etc).
const TIMEOUT_MS = 15000;

const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(max, v));

const easeOut = (x: number): number => {
  const t = x - 1;
  return t * t * t + 1;
};

function initLoader(wrap: HTMLElement): void {
  const video = wrap.querySelector('video');
  const loader = wrap.querySelector<HTMLElement>('.ix-loader');
  const mark = wrap.querySelector<HTMLElement>('.ix-mark');
  if (!video || !loader || !mark) {
    return;
  }

  const groups = Array.from(wrap.querySelectorAll<SVGGElement>('.ixc'));

  const t0 = performance.now();
  let ready = false;
  let revealAt: number | null = null;
  let stopAt: number | null = null;

  const reveal = (): void => {
    if (ready) return;
    ready = true;
    loader.classList.add('ix-ready');
    // Only force playback if the author asked for autoplay; respect their muted setting.
    if (video.autoplay) {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }
    stopAt = performance.now() + 1000;
  };

  video.addEventListener('loadedmetadata', () => {
    if (video.videoWidth && video.videoHeight) {
      wrap.style.aspectRatio = `${video.videoWidth} / ${video.videoHeight}`;
    }
  });

  const frame = (now: number): void => {
    const t = (now - t0) / 1000;

    groups.forEach((g) => {
      const dx = Number(g.dataset.dx ?? 0);
      const dy = Number(g.dataset.dy ?? 0);
      const delay = Number(g.dataset.delay ?? 0);
      const p = easeOut(clamp((t - delay) / 1.4, 0, 1));
      const tx = dx * 320 * (1 - p);
      const ty = dy * 320 * (1 - p);
      const rot = -150 * (1 - p);
      const s = 0.42 + 0.58 * p;
      g.style.transformBox = 'view-box';
      g.style.transformOrigin = '300px 300px';
      g.style.transform = `translate(${tx}px, ${ty}px) rotate(${rot}deg) scale(${s})`;
      g.style.opacity = String(clamp(p * 1.5, 0, 1));
    });

    if (!ready && revealAt === null && video.readyState >= 3) {
      revealAt = Math.max(now, t0 + MIN_MS);
    }
    if (revealAt !== null && now >= revealAt) {
      reveal();
    }
    if (!ready && now - t0 >= TIMEOUT_MS) {
      reveal();
    }

    if (stopAt === null || now < stopAt) {
      requestAnimationFrame(frame);
    }
  };

  requestAnimationFrame(frame);
}

function initPlayOverlay(wrap: HTMLElement): void {
  const video = wrap.querySelector<HTMLVideoElement>('video');
  const button = wrap.querySelector<HTMLButtonElement>('button.wp-block-interact-media__play');
  if (!video || !button) {
    return;
  }

  // `data-started` is a one-way flag: once playback has begun, the wrapper's
  // poster background should never reappear (even on pause/end), since the
  // <video> element keeps its last frame and the poster behind it would
  // bleed through. `data-player-active` is the overlay visibility flag.
  const markStarted = (): void => {
    video.removeAttribute('poster');
    wrap.dataset.started = 'true';
  };

  const play = (): void => {
    markStarted();
    const promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(() => {
        // Autoplay denial — leave the overlay visible so the user can retry.
        wrap.dataset.playerActive = 'false';
      });
    }
  };

  button.addEventListener('click', play);

  // Clicking the video toggles play/pause. When playing, the overlay is hidden
  // (pointer-events:none) so this click hits the video element directly.
  video.addEventListener('click', () => {
    if (video.paused) {
      play();
    } else {
      video.pause();
    }
  });

  // Sync the overlay state to the actual playback state so the overlay
  // reappears whenever the video pauses (via click, end of unloopable video,
  // tab visibility change, etc.).
  video.addEventListener('play', () => {
    wrap.dataset.playerActive = 'true';
    markStarted();
  });
  video.addEventListener('pause', () => {
    wrap.dataset.playerActive = 'false';
  });

  // If autoplay fired before this listener attached (HTML-parsed video can
  // start playing before DOMContentLoaded), sync state immediately so the
  // overlay doesn't sit on top of an already-playing video.
  if (!video.paused) {
    wrap.dataset.playerActive = 'true';
    markStarted();
  }
}

function bootstrap(): void {
  document
    .querySelectorAll<HTMLElement>('.wp-block-interact-media[data-loader="branded"]')
    .forEach(initLoader);
  document
    .querySelectorAll<HTMLElement>('.wp-block-interact-media[data-player="video"]')
    .forEach(initPlayOverlay);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
} else {
  bootstrap();
}
